import { isResource, resolveId } from '@medplum/core'
import type {
  CodeableConcept,
  Device,
  Flag,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '#src/codeableConcept.ts'
import { FLAG_NOTES_EXTENSION } from '#src/codeSystems.ts'
import { Extension } from '#src/decorators.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { isOfType } from '#src/utils.ts'

export type FlagPriority = 'P0' | 'P1' | 'P2' | 'P3'
export type FlagStatus = 'active' | 'inactive' | 'entered-in-error'

export class ExtendedFlag extends ExtendedResource<Flag> {
  static readonly RESOURCE_TYPE = 'Flag'

  static isInstance(value: unknown): value is Flag {
    return isResource(value) && isOfType(value, 'Flag')
  }

  get status(): FlagStatus {
    return this.resource.status as FlagStatus
  }

  set status(status: FlagStatus) {
    this.resource.status = status
  }

  get priority(): FlagPriority | undefined {
    return this.resource.meta?.tag?.[0]?.code as FlagPriority
  }

  set priority(priority: FlagPriority | undefined) {
    if (priority) {
      this.resource.meta = {
        ...this.resource.meta,
        tag: [
          {
            system: 'http://your-org.com/flag-priority',
            code: priority,
          },
        ],
      }
    }
  }

  get subject(): Reference<Patient> {
    return this.resource.subject as Reference<Patient>
  }

  set subject(subject: Reference<Patient>) {
    this.resource.subject = subject
  }

  get code(): string {
    return codeFromCodeableConcept(this.resource.code) ?? ''
  }

  get display(): string {
    return displayFromCodeableConcept(this.resource.code) ?? ''
  }

  get start(): string | undefined {
    return this.resource.period?.start
  }

  set start(start: string | undefined) {
    if (!start && !this.resource.period?.end) {
      this.resource.period = undefined
      return
    }
    this.resource.period = { ...this.resource.period, start }
  }

  get end(): string | undefined {
    return this.resource.period?.end
  }

  set end(end: string | undefined) {
    if (!end && !this.resource.period?.start) {
      this.resource.period = undefined
      return
    }
    if (!end) {
      const start = this.resource.period?.start
      this.resource.period = start ? { start } : undefined
      return
    }
    this.resource.period = { ...this.resource.period, end }
  }

  get reason(): string {
    return this.resource.code?.text ?? ''
  }

  set reason(reason: string) {
    this.resource.code = {
      ...this.resource.code,
      coding: this.resource.code?.coding,
      text: reason || undefined,
    }
  }

  @Extension({
    url: FLAG_NOTES_EXTENSION,
    kind: 'valueString',
  })
  notes?: string

  get category(): CodeableConcept[] | undefined {
    return this.resource.category
  }

  get categoryCodes(): string[] | undefined {
    return this.category?.map((c) => codeFromCodeableConcept(c) ?? '')
  }

  get author():
    | Reference<
        Patient | Organization | Practitioner | Device | PractitionerRole
      >
    | undefined {
    return this.resource.author
  }

  protected getExportFields() {
    return {
      id: this.id,
      status: this.status,
      priority: this.priority,
      code: this.code,
      display: this.display,
      patientId: resolveId(this.subject),
      category: this.categoryCodes,
      start: this.start,
      end: this.end,
      authorId: resolveId(this.author),
    }
  }
}
